/**
 * Privacy Policy — required for underwriter approval and for CCPA/GDPR-adjacent
 * disclosures. Describes exactly what data we collect, what we do with it, who
 * we share it with, and how buyers can request access or deletion.
 */
export default function Privacy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="privacy-page">
      <div className="eyebrow">Legal</div>
      <h1 className="h-display text-4xl md:text-6xl mt-2 leading-[1.04]">Privacy Policy.</h1>
      <p className="mt-4 text-[13px] font-mono uppercase tracking-[0.18em] text-[var(--ink-muted)]">
        Effective February 1, 2026
      </p>

      <div className="mt-6 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4 text-[var(--ink-soft)] text-sm leading-relaxed">
        Nebula Aminos ("we", "us", "Company") respects your privacy. This policy describes the
        personal information we collect, how we use it, who we share it with, and the rights you
        have over your data.
      </div>

      <div className="mt-12 space-y-10 text-[var(--ink-soft)]">
        <Section title="1. Information we collect">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Account data:</strong> name, business email, organization, buyer type, hashed password.</li>
            <li><strong>Order data:</strong> items purchased, shipping address, phone number, order value, IP address at checkout, per-order attestations, and terms version.</li>
            <li><strong>Payment data:</strong> we do not store card numbers. Card payments are processed by our PCI-DSS compliant hosted processor (VERIFIED Crypto Checkout). We receive only a transaction reference. Native crypto payments record the transaction hash and destination wallet.</li>
            <li><strong>Compliance data:</strong> buyer-type declaration, RUO/21+ attestations, and IP address to support age-gate and jurisdictional restrictions.</li>
            <li><strong>Technical data:</strong> cookies for authentication (httpOnly JWT), session, cart persistence, and referral capture (?ref=). We do not run third-party ad-tracking pixels.</li>
          </ul>
        </Section>

        <Section title="2. How we use it">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Fulfill orders, provide COAs, and send transactional email (order confirmation, shipment notification, refund).</li>
            <li>Verify buyer eligibility, enforce RUO/21+ restrictions, and screen orders for compliance flags.</li>
            <li>Prevent fraud, chargeback abuse, and unauthorized access.</li>
            <li>Operate the affiliate/loyalty programs (attribution and payout).</li>
            <li>Comply with legal obligations including recordkeeping, tax, and law-enforcement requests.</li>
          </ul>
        </Section>

        <Section title="3. Who we share it with">
          <p>We share the minimum data necessary with the following service providers, each bound
          by a data-processing agreement:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-2">
            <li><strong>VERIFIED Crypto Checkout</strong> — card acceptance &amp; settlement (payment processor).</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>USPS / UPS / FedEx</strong> — carrier fulfillment (shipping address only).</li>
            <li><strong>Koveralabs / third-party assay labs</strong> — batch-level COA data (no buyer PII).</li>
            <li><strong>Cloud hosting</strong> — Emergent (US-based).</li>
          </ul>
          <p className="mt-3">We do not sell your personal information. We do not share your data
          with advertisers. We may disclose your information when required by law, subpoena, or
          court order.</p>
        </Section>

        <Section title="4. Cookies">
          <p>We use strictly-necessary cookies for authentication (<code className="font-mono text-[12px]">access_token</code>,
          <code className="font-mono text-[12px]"> refresh_token</code>, both httpOnly), cart persistence, and referral
          capture. We do not use marketing or advertising cookies. You may disable cookies in your
          browser, but the site will not function without the authentication cookies.</p>
        </Section>

        <Section title="5. Data retention">
          <p>Order records, buyer attestations, and audit logs are retained for seven (7) years
          to satisfy US recordkeeping requirements. Account data is retained until you request
          deletion. Marketing consents are retained for the duration of your account.</p>
        </Section>

        <Section title="6. Your rights (US &amp; adjacent)">
          <p>Depending on your jurisdiction, you may have the right to (a) request access to your
          personal data, (b) request correction or deletion (subject to our recordkeeping
          obligations), (c) opt out of any sale of personal information — we do not sell, and
          (d) receive a portable copy of your data.</p>
          <p className="mt-3">To exercise any of these rights, email{" "}
            <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a>{" "}
            from the address on file. We respond within 30 days.</p>
        </Section>

        <Section title="7. Security">
          <p>Site-wide TLS 1.3 (HTTPS). Passwords are hashed with bcrypt. Tokens are httpOnly and
          same-site. Card data never touches our servers. Access to production data is
          restricted to authorized personnel and audit-logged.</p>
        </Section>

        <Section title="8. Children">
          <p>Nebula Aminos is not directed to individuals under 21. We do not knowingly collect
          personal data from anyone under 21. If you believe we have collected data from a minor,
          contact us immediately and we will delete it.</p>
        </Section>

        <Section title="9. Changes to this policy">
          <p>We may update this Privacy Policy from time to time. The effective date at the top of
          the page indicates when the current version took effect.</p>
        </Section>

        <Section title="10. Contact">
          <p>Nebula Aminos · 113 W Ryker Ln, Midvale UT 84047, USA · 305-877-7739 ·{" "}
            <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a>.</p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-medium text-[var(--ink)]">{title}</h2>
      <div className="mt-3 leading-relaxed text-[var(--ink-soft)] space-y-3">{children}</div>
    </section>
  );
}
